if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.desktopMediaDragMql = window.matchMedia('(min-width: 990px)');
        this.pointerFineMediaQuery = window.matchMedia('(pointer: fine)');
        this.desktopDragState = {
          isPointerDown: false,
          startX: 0,
          hasDragged: false,
          pointerId: null,
        };
        this.desktopDragPreventClick = false;
        this.desktopWheelLock = false;
        this.desktopPointerDownHandler = this.onDesktopPointerDown.bind(this);
        this.desktopPointerMoveHandler = this.onDesktopPointerMove.bind(this);
        this.desktopPointerUpHandler = this.onDesktopPointerUp.bind(this);
        this.desktopClickHandler = this.onDesktopClick.bind(this);
        this.desktopWheelHandler = this.onDesktopWheel.bind(this);
        this.bindDesktopDragEvents();
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          // Vertical page auto-scroll removed: selecting a fabric swatch (which
          // activates a gallery image) must not jump the page back up to the gallery.
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }

      bindDesktopDragEvents() {
        // Desktop click-drag / wheel swipe on the main gallery photo is disabled
        // by request (it let users drag the large photo to change images). Image
        // navigation still works via the fabric swatches and the prev/next
        // buttons. Returning here skips all pointer/wheel/click drag listeners.
        return;
        // eslint-disable-next-line no-unreachable
        if (!this.elements.viewer) return;
        this.desktopDragTarget = this.elements.viewer.querySelector('.product__media-list');
        if (!this.desktopDragTarget || this.desktopDragTarget.dataset.desktopDragBound === 'true') return;

        this.desktopDragTarget.dataset.desktopDragBound = 'true';
        this.desktopDragTarget.addEventListener('pointerdown', this.desktopPointerDownHandler);
        this.desktopDragTarget.addEventListener('pointermove', this.desktopPointerMoveHandler);
        this.desktopDragTarget.addEventListener('pointerup', this.desktopPointerUpHandler);
        this.desktopDragTarget.addEventListener('pointerleave', this.desktopPointerUpHandler);
        this.desktopDragTarget.addEventListener('pointercancel', this.desktopPointerUpHandler);
        this.desktopDragTarget.addEventListener('click', this.desktopClickHandler, true);
        this.desktopDragTarget.addEventListener('wheel', this.desktopWheelHandler, { passive: false });
      }

      onDesktopPointerDown(event) {
        if (!this.shouldHandleDesktopSwipe(event)) return;
        event.preventDefault();
        this.desktopDragPreventClick = false;
        this.desktopDragState.isPointerDown = true;
        this.desktopDragState.startX = event.clientX;
        this.desktopDragState.hasDragged = false;
        this.desktopDragState.pointerId = event.pointerId;
        this.desktopDragTarget.classList.add('is-pointer-dragging');
        this.desktopDragTarget.style.userSelect = 'none';

        if (this.desktopDragTarget.setPointerCapture) {
          try {
            this.desktopDragTarget.setPointerCapture(event.pointerId);
          } catch (error) {
            // setPointerCapture can throw in some browsers; safe to ignore
          }
        }
      }

      onDesktopPointerMove(event) {
        if (!this.desktopDragState.isPointerDown) return;
        const deltaX = event.clientX - this.desktopDragState.startX;

        if (Math.abs(deltaX) > 6) {
          this.desktopDragState.hasDragged = true;
          event.preventDefault();
        }
      }

      onDesktopPointerUp(event) {
        if (!this.desktopDragState.isPointerDown) return;

        if (this.desktopDragTarget.releasePointerCapture && this.desktopDragState.pointerId !== null) {
          try {
            this.desktopDragTarget.releasePointerCapture(this.desktopDragState.pointerId);
          } catch (error) {
            // releasePointerCapture can throw if capture is not set; safe to ignore
          }
        }

        const deltaX = event.clientX - this.desktopDragState.startX;
        const swipeThreshold = 20;

        if (this.desktopDragState.hasDragged && Math.abs(deltaX) > swipeThreshold) {
          const direction = deltaX < 0 ? 'next' : 'previous';
          this.moveToAdjacentMedia(direction);
          this.desktopDragPreventClick = true;
        }

        this.desktopDragState = {
          isPointerDown: false,
          startX: 0,
          hasDragged: false,
          pointerId: null,
        };
        this.desktopDragTarget.classList.remove('is-pointer-dragging');
        this.desktopDragTarget.style.userSelect = '';
      }

      onDesktopClick(event) {
        if (!this.desktopDragPreventClick) return;
        event.preventDefault();
        event.stopPropagation();
        this.desktopDragPreventClick = false;
      }

      moveToAdjacentMedia(direction) {
        const mediaOrder = this.getMediaOrder();
        if (!mediaOrder.length) return;

        const currentMediaId = this.getCurrentMediaId(mediaOrder);
        const currentIndex = mediaOrder.indexOf(currentMediaId);
        if (currentIndex === -1) return;

        const targetIndex = direction === 'next' ? currentIndex + 1 : currentIndex - 1;
        if (targetIndex < 0 || targetIndex >= mediaOrder.length) return;

        this.setActiveMedia(mediaOrder[targetIndex], false);
      }

      getMediaSlides() {
        if (!this.elements.viewer) return [];
        return Array.from(this.elements.viewer.querySelectorAll('[data-media-id]'));
      }

      shouldHandleDesktopSwipe(event) {
        const isDesktop = this.desktopMediaDragMql?.matches;
        const hasFinePointer = this.pointerFineMediaQuery?.matches;
        const hasMultipleSlides = this.getMediaSlides().length > 1;
        const isPrimaryPointer =
          (event?.pointerType === 'mouse' || event?.pointerType === 'pen' || event?.pointerType === 'touch') &&
          event.button === 0;

        return isDesktop && hasFinePointer && hasMultipleSlides && isPrimaryPointer;
      }

      shouldHandleDesktopWheel(event) {
        if (event.ctrlKey) return false;
        const isDesktop = this.desktopMediaDragMql?.matches;
        const hasFinePointer = this.pointerFineMediaQuery?.matches;
        const hasMultipleSlides = this.getMediaSlides().length > 1;
        const isMostlyHorizontal = Math.abs(event.deltaX) > Math.abs(event.deltaY);
        const hasMeaningfulDelta = Math.abs(event.deltaX) > 10;

        return isDesktop && hasFinePointer && hasMultipleSlides && isMostlyHorizontal && hasMeaningfulDelta;
      }

      onDesktopWheel(event) {
        if (!this.shouldHandleDesktopWheel(event)) return;
        event.preventDefault();

        if (this.desktopWheelLock) return;
        this.desktopWheelLock = true;
        const direction = event.deltaX > 0 ? 'next' : 'previous';
        this.moveToAdjacentMedia(direction);
        window.setTimeout(() => {
          this.desktopWheelLock = false;
        }, 220);
      }

      getMediaOrder() {
        if (this.elements.thumbnails) {
          return Array.from(this.elements.thumbnails.querySelectorAll('[data-target]'))
            .map((thumbnail) => thumbnail.dataset.target)
            .filter(Boolean);
        }

        return this.getMediaSlides()
          .map((slide) => slide.dataset.mediaId)
          .filter(Boolean);
      }

      getCurrentMediaId(order) {
        if (this.elements.thumbnails) {
          const activeThumb = this.elements.thumbnails.querySelector('[aria-current="true"]');
          const targetFromThumb = activeThumb?.closest('[data-target]')?.dataset.target;
          if (targetFromThumb) return targetFromThumb;
        }

        const activeSlide = this.getMediaSlides().find((slide) => slide.classList.contains('is-active'));
        if (activeSlide?.dataset.mediaId) return activeSlide.dataset.mediaId;

        return order?.[0];
      }
    }
  );
}
